import React, { useState, useEffect } from 'react';
import { ShieldCheck, Lock, FileText, Scale, UserCheck, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import PageLayout from '../components/PageLayout';

const Agreement = () => {
  const [accepted, setAccepted] = useState(false);
  const [activeSection, setActiveSection] = useState('terms');

  // Handle scroll detection for sidebar highlighting
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['terms', 'privacy', 'ai-ethics', 'kids-safety'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'terms', title: 'Terms of Service', icon: <Scale size={18} /> },
    { id: 'privacy', title: 'Privacy Policy', icon: <Lock size={18} /> },
    { id: 'ai-ethics', title: 'AI & Data Ethics', icon: <ShieldCheck size={18} /> },
    { id: 'kids-safety', title: 'Youth Safety', icon: <UserCheck size={18} /> }
  ];

  return (
    <PageLayout>
      <div className="min-h-screen bg-[#F8FAFC] font-sans">
        {/* --- HEADER --- */}
        <div className="bg-white border-b border-slate-200 pt-16 pb-12">
          <div className="max-w-6xl mx-auto px-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest mb-6">
              <ShieldCheck size={12} /> Legal Compliance v2.1
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight">
              Our <span className="text-blue-600">Framework.</span>
            </h1>
            <p className="text-slate-500 mt-4 max-w-2xl text-lg font-medium">
              We believe in transparent AI orchestration. This document outlines how we protect your data and your digital spiritual journey.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col lg:flex-row gap-12">
          
          {/* --- SIDEBAR NAVIGATION --- */}
          <aside className="lg:w-72 shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4 px-4">Navigation</p>
              {sections.map((sec) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-300 group ${
                    activeSection === sec.id 
                    ? 'bg-white shadow-md text-blue-600 border border-slate-100' 
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-3 font-bold text-sm">
                    <span className={activeSection === sec.id ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}>
                      {sec.icon}
                    </span>
                    {sec.title}
                  </div>
                  {activeSection === sec.id && <ChevronRight size={14} className="animate-pulse" />}
                </a>
              ))}
            </div>
          </aside>

          {/* --- CONTENT AREA --- */}
          <div className="flex-1 space-y-20 pb-32">
            
            {/* 1. Terms of Service */}
            <section id="terms" className="scroll-mt-28">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100">
                  <Scale size={24} />
                </div>
                <h2 className="text-3xl font-black text-slate-900">Terms of Service</h2>
              </div>
              <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm leading-relaxed text-slate-600">
                <div className="mb-6 bg-slate-50 p-6 rounded-3xl border-l-4 border-indigo-500">
                  <p className="font-bold text-slate-800 text-sm mb-1">Key Takeaway:</p>
                  <p className="text-xs">Mindslog is a platform for lawful intelligence gathering. No commercial scraping allowed.</p>
                </div>
                <p className="mb-4">By accessing <strong>Mindslog.com</strong> and its subdomains, you agree to use our Agentic tools for lawful purposes. Our ecosystem is built to enhance human cognitive capability, not to replace ethical decision-making.</p>
                <ul className="space-y-3 mt-6">
                  {['Personal use only', 'No unauthorized data mining', 'Responsible agent orchestration'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                      <CheckCircle2 size={16} className="text-green-500" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* 2. Privacy Policy */}
            <section id="privacy" className="scroll-mt-28">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100">
                  <Lock size={24} />
                </div>
                <h2 className="text-3xl font-black text-slate-900">Privacy & Data</h2>
              </div>
              <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm leading-relaxed text-slate-600">
                <p>We prioritize <strong>Local-First storage</strong>. Your personal "Soul-Bot" progress and technical preferences are encrypted locally whenever possible.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                  <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">
                    <p className="font-bold text-slate-900 mb-2">What we store</p>
                    <p className="text-xs">Anonymized IQ trends, login credentials, and public directory contributions.</p>
                  </div>
                  <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">
                    <p className="font-bold text-slate-900 mb-2">What we don't</p>
                    <p className="text-xs">Individual spiritual choices, private chat contents, or raw behavioral biometric data.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. AI Ethics */}
            <section id="ai-ethics" className="scroll-mt-28">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-cyan-50 flex items-center justify-center text-cyan-600 border border-cyan-100">
                  <ShieldCheck size={24} />
                </div>
                <h2 className="text-3xl font-black text-slate-900">AI & Agentic Ethics</h2>
              </div>
              <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm leading-relaxed text-slate-600">
                <p className="italic text-lg text-slate-500 mb-6">"Our agents work for you, not the other way around."</p>
                <p>While we use <strong>Autonomous Agents</strong> for data indexing, all critical corporate entries undergo "Human-in-the-loop" verification. We do not allow agents to perform biased profiling or exclusionary filtering based on protected identity markers.</p>
              </div>
            </section>

            {/* 4. Youth Safety */}
            <section id="kids-safety" className="scroll-mt-28">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100">
                  <UserCheck size={24} />
                </div>
                <h2 className="text-3xl font-black text-slate-900">Youth Safety</h2>
              </div>
              <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm leading-relaxed text-slate-600">
                <div className="flex gap-4 p-6 bg-amber-50 rounded-3xl border border-amber-100 text-amber-900 mb-6">
                  <AlertCircle className="shrink-0" />
                  <p className="text-sm font-medium">MindLabs IQ is strictly monitored for users under 13. Parental override is required for persistent profile saving.</p>
                </div>
                <p>Our cognitive games are designed to be universally inclusive. We avoid divisive content and focus purely on cognitive ethics and universal logic patterns.</p>
              </div>
            </section>

            {/* --- FINAL ACTION BOX --- */}
            <div className={`mt-20 p-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 rounded-[3rem] transition-all duration-700 ${accepted ? 'scale-95 opacity-80' : 'scale-100'}`}>
              <div className="bg-slate-900 rounded-[2.9rem] p-8 md:p-12 text-white text-center">
                <h3 className="text-2xl md:text-3xl font-black mb-4">Accept the Mindslog Protocol?</h3>
                <p className="text-slate-400 mb-10 max-w-lg mx-auto">Clicking accept confirms you have read our framework and agree to our data handling standards for 2026.</p>
                
                <button 
                  onClick={() => setAccepted(!accepted)}
                  className={`group relative inline-flex items-center gap-3 px-10 py-4 rounded-full font-black text-sm tracking-widest uppercase transition-all duration-300 ${
                    accepted 
                    ? 'bg-green-500 text-white hover:bg-green-400' 
                    : 'bg-white text-slate-900 hover:bg-blue-50 hover:scale-105 shadow-[0_0_20px_rgba(255,255,255,0.2)]'
                  }`}
                >
                  {accepted ? (
                    <>Agreement Signed <CheckCircle2 size={18} /></>
                  ) : (
                    <>I Accept & Continue <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" /></>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Agreement;